import axios from "axios";

const rawApiUrl = import.meta.env.VITE_API_URL || "https://myfreezer.akzaatz.com";
const API_URL = rawApiUrl.endsWith("/api") ? rawApiUrl : `${rawApiUrl}/api`;

// Créer une instance axios avec les configurations par défaut
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Intercepteur pour ajouter le token aux requêtes
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Erreur API:", error.response?.data || error.message);
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      // Émettre un événement personnalisé pour la redirection
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    // Gérer les erreurs 500
    if (error.response?.status === 500) {
      console.error("Erreur serveur:", error.response.data);
      throw new Error(
        "Une erreur est survenue sur le serveur. Veuillez réessayer plus tard."
      );
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Inscription
  async register(userData) {
    try {
      const response = await api.post("/auth/register", {
        username: userData.username,
        email: userData.email,
        password: userData.password,
      });
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      return { token, user };
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Échec de l'inscription"
      );
    }
  },

  // Connexion
  async login(credentials) {
    try {
      const response = await api.post("/auth/login", {
        username: credentials.username,
        password: credentials.password,
      });
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      return { token, user };
    } catch (error) {
      throw new Error(error.response?.data?.message || "Échec de la connexion");
    }
  },

  // Déconnexion
  logout() {
    localStorage.removeItem("token");
  },

  // Mettre à jour le profil
  async updateProfile(updates) {
    try {
      const response = await api.patch("/auth/profile", updates);
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Échec de la mise à jour du profil"
      );
    }
  },

  // Supprimer le compte
  async deleteAccount() {
    try {
      await api.delete("/auth/profile");
      localStorage.removeItem("token");
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Échec de la suppression du compte"
      );
    }
  },

  // Récupérer les informations de l'utilisateur
  async getUserProfile() {
    try {
      const response = await api.get("/auth/profile");
      return response.data.user;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Échec de la récupération du profil"
      );
    }
  },

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Vérifier si le token est expiré
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      const { exp } = JSON.parse(jsonPayload);
      if (exp * 1000 < Date.now()) {
        this.logout();
        return false;
      }
      return true;
    } catch (error) {
      console.error("Erreur lors de la vérification du token:", error);
      this.logout();
      return false;
    }
  },

  // Obtenir le token
  getToken() {
    return localStorage.getItem("token");
  },

  // Vérifier la validité du token et obtenir les informations de l'utilisateur
  async validateToken() {
    try {
      const response = await api.get("/auth/validate");
      return response.data.user;
    } catch (error) {
      this.logout();
      throw error;
    }
  },

  async forgotPassword(email) {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la demande de réinitialisation"
      );
    }
  },

  async resetPassword(token, password) {
    try {
      const response = await api.post("/auth/reset-password", { token, password });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erreur lors de la réinitialisation"
      );
    }
  },
};

export default api;
